<template>
  <div class="login-container">
    <section class="login-brand">
      <div class="brand-mark">W</div>
      <h1>汪汪职道后台管理系统</h1>
      <p>管理用户、简历服务、支付记录和运营数据</p>
    </section>

    <el-form
      class="login-form"
      ref="loginFromRef"
      :model="loginForm"
      :rules="loginRules"
    >
      <div class="title-container">
        <h2 class="title">账号登录</h2>
        <p class="subtitle">请输入管理员账号继续</p>
      </div>

      <el-form-item prop="username">
        <span class="svg-container">
          <svg-icon icon="user" />
        </span>
        <el-input
          placeholder="请输入用户名"
          name="username"
          type="text"
          v-model="loginForm.username"
        />
      </el-form-item>

      <el-form-item prop="password">
        <span class="svg-container">
          <svg-icon icon="password" />
        </span>
        <el-input
          placeholder="请输入密码"
          name="password"
          :type="passwordType"
          v-model="loginForm.password"
        />
        <span class="show-pwd">
          <svg-icon
            :icon="passwordType === 'password' ? 'eye' : 'eye-open'"
            @click="onChangePwdType"
          />
        </span>
      </el-form-item>

      <el-button
        type="primary"
        class="login-submit"
        :loading="loading"
        @click="handleLogin"
      >
        登录
      </el-button>
    </el-form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { validatePassword } from './rules'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

// 数据源
const loginForm = ref({
  username: '',
  password: ''
})
// 验证规则
const loginRules = ref({
  username: [
    {
      required: true,
      trigger: 'blur',
      message: computed(() => {
        return '请输入用户名'
      })
    }
  ],
  password: [
    {
      required: true,
      trigger: 'blur',
      validator: validatePassword()
    }
  ]
})

// 处理密码框文本显示状态
const passwordType = ref('password')
const onChangePwdType = () => {
  if (passwordType.value === 'password') {
    passwordType.value = 'text'
  } else {
    passwordType.value = 'password'
  }
}

// 登录动作处理
const loading = ref(false)
const loginFromRef = ref(null)
const store = useStore()
const router = useRouter()
const handleLogin = () => {
  loginFromRef.value.validate((valid) => {
    if (!valid) return
    loading.value = true
    store
      .dispatch('user/login', loginForm.value)
      .then(() => {
        loading.value = false
        // 登录后操作
        router.push('/')
      })
      .catch(() => {
        loading.value = false
      })
  })
}
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(360px, 0.9fr) minmax(420px, 1.1fr);
  background:
    linear-gradient(90deg, #0f172a 0%, #111827 42%, #f6f8fb 42%),
    #f6f8fb;
  overflow: hidden;

  .login-brand {
    min-height: 100vh;
    padding: 72px 56px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: #fff;

    .brand-mark {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 28px;
      background: #2563eb;
      color: #fff;
      font-size: 24px;
      font-weight: 800;
    }

    h1 {
      margin: 0;
      max-width: 420px;
      font-size: 34px;
      line-height: 44px;
      letter-spacing: 0;
      font-weight: 800;
    }

    p {
      max-width: 360px;
      margin: 16px 0 0;
      color: #cbd5e1;
      font-size: 15px;
      line-height: 24px;
    }
  }

  .login-form {
    position: relative;
    align-self: center;
    width: 420px;
    max-width: 100%;
    padding: 40px;
    margin: 0 auto;
    overflow: hidden;
    background: #fff;
    border: 1px solid var(--admin-color-border-soft);
    border-radius: 12px;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);

    ::v-deep .el-form-item {
      margin-bottom: 18px;
      border: 1px solid var(--admin-color-border);
      background: #fff;
      border-radius: 8px;
      color: var(--admin-color-text);
      overflow: hidden;
    }

    ::v-deep .el-input {
      display: inline-block;
      height: 47px;
      width: 85%;

      .el-input__wrapper {
        background: transparent !important;
        box-shadow: none !important;
      }

      input {
        background: transparent;
        border: 0px;
        -webkit-appearance: none;
        border-radius: 0px;
        padding: 12px 5px 12px 15px;
        color: var(--admin-color-text);
        height: 47px;
        caret-color: var(--admin-color-primary);
      }
    }
  }

  .tips {
    font-size: 16px;
    line-height: 28px;
    color: #fff;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .svg-container {
    padding: 6px 5px 6px 15px;
    color: var(--admin-color-text-muted);
    vertical-align: middle;
    display: inline-block;
  }

  .title-container {
    position: relative;

    .title {
      font-size: 24px;
      color: var(--admin-color-text);
      margin: 0;
      text-align: left;
      font-weight: 800;
    }

    .subtitle {
      margin: 8px 0 28px;
      color: var(--admin-color-text-muted);
      font-size: 14px;
      line-height: 20px;
    }

    ::v-deep .lang-select {
      position: absolute;
      top: 4px;
      right: 0;
      background-color: white;
      font-size: 22px;
      padding: 4px;
      border-radius: 4px;
      cursor: pointer;
    }
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: var(--admin-color-text-muted);
    cursor: pointer;
    user-select: none;
  }

  .login-submit {
    width: 100%;
    height: 40px;
    margin-bottom: 0;
  }
}

@media (max-width: 900px) {
  .login-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #f6f8fb;

    .login-brand {
      display: none;
    }

    .login-form {
      width: 100%;
      max-width: 420px;
      padding: 28px;
    }
  }
}
</style>
